import {
  Background,
  BackgroundVariant,
  ControlButton,
  Controls,
  Edge,
  MarkerType,
  Node,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow
} from '@xyflow/react';
import clsx from 'clsx';
import React, {
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { AiOutlineInfoCircle, AiOutlineMinusCircle, AiOutlinePlusCircle } from 'react-icons/ai';
import { FaRegEdit } from 'react-icons/fa';
import { FiMessageCircle, FiPlusCircle } from 'react-icons/fi';
import { LuPackagePlus } from 'react-icons/lu';
import { MdOutlineCenterFocusStrong } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Tab,
  TabList,
  TabPanel,
  Tabs
} from 'react-tabs';
import { toast,ToastContainer } from 'react-toastify';
import 'react-tabs/style/react-tabs.css';
import 'react-toastify/dist/ReactToastify.css';
import '@xyflow/react/dist/style.css';

import APIGateway from '@/api';
import { Button } from '@/components/button/button';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Loader } from '@/components/loader/loader';
import LowCodeChat from '@/components/lowCodeChat/lowCodeChat';
import { useAgentConfigQuery } from '@/hooks/queries/useAgents';
import { useModelsQuery } from '@/hooks/queries/useModelsQuery';
import {
  AgentConfig,
  AgentConfigStep,
  AgentConfigStepLabelEnum,
  AgentConfigStepTypeEnum,
  AgentTypeEnum,
  AgentVersionUpdateArgs
} from '@/types/agent';
import { Model } from '@/types/model';
import { parseParams } from '@/utils/searchParams';

import { ConfigEditPanel } from './components/configEditPanel/configEditPanel';
import { AgentSmartEdge } from './components/edge';
import { BasicNode,LowCodeNode } from './components/node';
import PerformanceView from './components/performance';
import TraceView from './components/trace';
import { SaveEditsModal } from './modals/SaveEditsModal';
import { VersionChangeModal } from './modals/VersionChangeModal';
import { VersionCreateModal } from './modals/VersionCreateModal';
import styles from './page.module.scss';
import { AgentEditProvider, useAgentEdit } from './state/agentEditContext';
import { AgentEditActionType } from './state/agentEditReducer';
import {
  ADDABLE_NODE_TYPES,
  getNodeTypeAddDescription,
  getNodeTypeIcon,
  getNodeTypeLabel
} from './util/nodeTypeUtils';
import { createStep } from './util/stepFactory';
import {
  buildValidationErrorMap,
  hasValidationErrors,
  validateAllSteps,
  ValidationErrorMap
} from './util/validation';


const nodeTypes = { lowCodeNode: LowCodeNode, basicNode: BasicNode };
const edgeTypes = { smart: AgentSmartEdge };

// Default positions for nodes without saved positions
const DEFAULT_NODE_POSITION = { x: 350, y: 100 };
const START_NODE_POSITION = { x: 100, y: 100 };
const END_NODE_POSITION = { x: 650, y: 100 };

// Custom hooks
const useAgentData = (
  propId: string,
  propVersion: string
) => {
  const navigate = useNavigate();

  const { data: configData, isPending: isConfigLoading, error: configError } = useAgentConfigQuery(
    propId,
    propVersion ? parseInt( propVersion ) : undefined
  );

  const { data: modelsData, isPending: isModelsLoading } = useModelsQuery( { enabled: true } );

  const models = modelsData?.map( ( model: Model ) => model.name ) || [];
  const isLoading = isConfigLoading || isModelsLoading;

  useEffect( () => {
    if ( configError ) {
      console.error( configError );
      toast.error( configError.message );
      navigate( '/agents/', { replace: true } );
    }
  }, [ configError, navigate ] );

  return { configData, models, isLoading };
};

const useGraphBuilder = (
  config: AgentConfig | undefined,
  validationErrors: ValidationErrorMap,
  shouldRebuild: boolean = true
) => {
  const [ edges, setEdges, onEdgesChange ] = useEdgesState<Edge>( [] );
  const [ nodes, setNodes, onNodesChange ] = useNodesState<Node<AgentConfigStep>>( [] );

  const buildGraph = ( steps: AgentConfigStep[] ) => {
    console.log( 'Rebuilding Graph' );

    const newNodes: Node<AgentConfigStep>[] = steps.map( ( step ) => {
      // Determine position: use saved position, or specific defaults for start/end nodes
      let position = step.position;
      if ( !position ) {
        if ( step.label === AgentConfigStepLabelEnum.START ) {
          position = START_NODE_POSITION;
        } else if ( step.label === AgentConfigStepLabelEnum.END ) {
          position = END_NODE_POSITION;
        } else {
          position = DEFAULT_NODE_POSITION;
        }
      }

      return {
        data: step,
        id: step.id,
        position,
        type: !step.label ? 'lowCodeNode' : 'basicNode'
      };
    } );

    const newEdges: Edge[] = [];

    steps.forEach( ( step, i ) => {
      Object.keys( step.routes ).forEach( ( route ) => {
        if ( step.routes[route].step && step.label !== AgentConfigStepLabelEnum.END ) {
          newEdges.push( {
            id: `${ i }-${ route }-post`,
            source: step.id,
            sourceHandle: `${ step.id }-out`,
            target: step.routes[route].step!,
            targetHandle: `${ step.routes[route].step }-in` // route step values are the step ids
          } );
        }
      } );
    } );

    setNodes( newNodes );
    setEdges( newEdges );
  };

  const updateNodeData = ( nodeId: string, newData: AgentConfigStep ) => {
    setNodes( ( nodes ) => nodes.map( ( node ) => {
      return node.id === nodeId ? { ...node, data: newData } : node;
    } )
    );
  };

  const addInitialSteps = (): AgentConfigStep[] => {
    const startStep: AgentConfigStep = {
      id: AgentConfigStepLabelEnum.START,
      label: AgentConfigStepLabelEnum.START,
      position: START_NODE_POSITION,
      routes: {}
    } as AgentConfigStep;

    const endStep: AgentConfigStep = {
      id: AgentConfigStepLabelEnum.END,
      label: AgentConfigStepLabelEnum.END,
      position: END_NODE_POSITION,
      routes: {}
    } as AgentConfigStep;

    return [ startStep, endStep ];
  };

  useEffect( () => {
    if ( !config || !shouldRebuild ) return;

    if ( !config.steps.length ) {
      const initialSteps = addInitialSteps();
      buildGraph( initialSteps );
    } else {
      buildGraph( config.steps );
    }
  }, [ config, shouldRebuild ] );

  // Update nodes when validation errors change to trigger re-render
  useEffect( () => {
    setNodes( ( nodes ) => nodes.map(
      ( node ) => ( {
        ...node,
        data: { ...node.data } // Force data reference change to trigger re-render
      } ) )
    );
  }, [ validationErrors, setNodes ] );

  return { nodes, setNodes, onNodesChange, edges, setEdges, onEdgesChange, updateNodeData };
};

// UI Components
const InfoView = ( {
  config,
  versionCurrent,
  versionNext,
  onDetails,
  onVersionChange,
  onTest,
  onCreateVersion,
  validationErrors
}: {
  config: AgentConfig;
  versionCurrent: number;
  versionNext: number | undefined;
  configIsEditable: boolean;
  onDetails: () => void;
  onVersionChange: ( version: string ) => void;
  onEdit: () => void;
  onTest: () => void;
  onCreateVersion: () => void;
  validationErrors: ValidationErrorMap;
} ) => {
  // Extract unavailable model errors
  const unavailableModels = Object.entries( validationErrors )
    .filter( ( [ _stepId, errors ] ) => errors.action?.model )
    .map( ( [ stepId, errors ] ) => ( { stepId, model: errors.action!.model } ) );

  return (
    <>
      <div className={ `layout__row ${ styles.info_header }` }>
        <span className={ styles.info_name }>
          <p>{ config.name }</p>
        </span>
        <span className={ `version ${ styles.info_version }` }>
          Version
          <select
            value={ versionNext || versionCurrent }
            onChange={ ( e ) => onVersionChange( e.target.value ) }
          >
            {
              config.versions.map(
                ( version: string, index: number ) => (
                  <option value={ version } key={ `version-option-${ index }` }>
                    {version}
                  </option>
                )
              )
            }
          </select>
        </span>
        <span className={ styles.info_actions }>
          <button onClick={ onDetails }>
            <AiOutlineInfoCircle /> Agent Details
          </button>
          <button onClick={ onCreateVersion }>
            <LuPackagePlus /> Create New Version
          </button>
          <button onClick={ onTest }>
            <FiMessageCircle /> Test
          </button>
        </span>
      </div>
      {unavailableModels.length > 0 && (
        <div className={ clsx( 'layout__row', 'pad' ) } style={ { backgroundColor: '#ffe6e6', padding: '0.75rem', borderLeft: '4px solid #ff4444' } }>
          <span style={ { color: '#d32f2f', fontWeight: 'bold' } }>
            ⚠️ Model Availability Error{unavailableModels.length > 1 ? 's' : ''}:
          </span>
          <ul style={ { margin: '0.5rem 0 0 1.5rem', color: '#d32f2f' } }>
            {unavailableModels.map( ( { stepId, model } ) => {
              const modelName = model.replace( 'Model "', '' ).replace( '" is not available', '' );
              return (
                <li key={ stepId }>
                  Node <strong>{stepId}</strong>: {modelName}
                </li>
              );
            } )}
          </ul>
        </div>
      )}
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <span className={ styles.info_description }>{config.description}</span>
      </div>
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <span className={ styles.info_url_label }>API Endpoint:</span>
        <span className={ styles.info_url }>{config.url}</span>
      </div>
    </>
  );
};

// Component that monitors container size and auto-fits view on resize
const FlowWrapper = React.memo(
  ( {
    containerRef,
    fitViewRef
  }: {
    containerRef: React.RefObject<HTMLDivElement | null>;
    fitViewRef: React.MutableRefObject<( () => void ) | null>;
  } ) => {
    const { fitView } = useReactFlow();

    useEffect( () => {
      // Store fitView function in ref for manual access
      fitViewRef.current = () => {
        fitView( { padding: 0.2 } );
      };

      const container = containerRef.current;
      if ( !container ) return;

      let lastSize = { width: 0, height: 0 };

      const resizeObserver = new ResizeObserver( () => {
        const { width, height } = container.getBoundingClientRect();

        // Always fit view when size changes (except initial measurement)
        if (
          lastSize.width !== 0 &&
          ( lastSize.width !== width || lastSize.height !== height )
        ) {
          setTimeout( () => fitView( { padding: 0.2 } ), 100 );
        }

        lastSize = { width, height };
      } );

      resizeObserver.observe( container );
      return () => resizeObserver.disconnect();
    }, [ fitView, containerRef, fitViewRef ] );

    return null;
  }
);

// Main component
type SearchParams = {
  id: string;
  version: string;
};

function AgentEditPage() {
  const [ searchParams ] = useSearchParams();
  const {
    id: qId,
    version: qVersion
  } = parseParams<SearchParams>( searchParams );
  const navigate = useNavigate();

  const addableNodeTypes = [ AgentConfigStepTypeEnum.LLM, AgentConfigStepTypeEnum.API, AgentConfigStepTypeEnum.AGENT ];

  // State
  const [ activeStepIndex, setActiveStepIndex ] = useState<number | undefined>();
  const [ configIsDirty, setConfigIsDirty ] = useState( false );
  const [ configIsEditable, setConfigIsEditable ] = useState( false );
  const [ isEditable, setIsEditable ] = useState( false );
  const [ isFullScreen, setIsFullScreen ] = useState( false );
  const [ versionCurrent, setVersionCurrent ] = useState( parseInt( qVersion ) );
  const [ versionNext, setVersionNext ] = useState<number | undefined>();
  const [ isPreview, setIsPreview ] = useState( false );
  const [ showSaveEditsModal, setShowSaveEditsModal ] = useState( false );
  const [ isSaving, setIsSaving ] = useState( false );
  const [ showVersionChangeModal, setShowVersionChangeModal ] = useState( false );
  const [ showCreateVersionModal, setShowCreateVersionModal ] = useState( false );

  // Custom hooks
  const { configData, models, isLoading } = useAgentData( qId, qVersion );
  const { config, dispatch, validationErrors, setValidationErrors } = useAgentEdit();

  const [ configOrig, setConfigOrig ] = useState<string | undefined>();
  const hasErrors = hasValidationErrors( validationErrors );

  // Initialize reducer when data loads
  useEffect( () => {
    if ( configData ) {
      dispatch( { type: AgentEditActionType.LOAD, config: configData } );
      setConfigOrig( JSON.stringify( configData ) );
      setValidationErrors( {} ); // Clear validation errors when config loads
    }
  }, [ configData, setValidationErrors ] );

  const { nodes, onNodesChange, edges, onEdgesChange } = useGraphBuilder( config ?? undefined, validationErrors, true );

  // ReactFlow instance for custom controls
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  // Refs
  const nameRef = useRef<HTMLInputElement>( null );
  const descriptionRef = useRef<HTMLTextAreaElement>( null );
  const fitViewRef = useRef<( () => void ) | null>( null );
  const containerRef = useRef<HTMLDivElement>( null );

  // Effects
  useEffect( () => {
    if ( !config ) return;

    setVersionCurrent( config.version );
    setConfigIsEditable(
      config.version.toString() !== config.versions[0]
        ? false
        : config.editable
    );

    // Reset state when switching versions
    setActiveStepIndex( undefined );
    setVersionNext( undefined );
    setIsSaving( false );
    setShowVersionChangeModal( false );
    setShowCreateVersionModal( false );
  }, [ config?.agentId, config?.version ] ); // Only run when config ID or version changes

  useEffect( () => {
    if ( !config ) return;

    checkIfDirty( config );
  }, [ config, configOrig ] );

  // Automatic validation - runs whenever config.steps, models, or context change
  useEffect( () => {
    if ( !config?.steps ) return;

    const errors = validateAllSteps( config.steps, models, config.context );
    const errorMap = buildValidationErrorMap( errors );
    setValidationErrors( prev => ( JSON.stringify( prev ) === JSON.stringify( errorMap ) ? prev : errorMap )
    );
  }, [ config?.steps, config?.context, models, setValidationErrors ] );

  // Utility functions
  const checkIfDirty = ( newConfig: AgentConfig ) => {
    if ( !configOrig ) return;
    const isDirty = JSON.stringify( newConfig ) !== configOrig;
    setConfigIsDirty( isDirty );
  };

  const getStepIndex = ( nodeId: string ) => config?.steps.findIndex( ( step ) => step.id === nodeId );

  const getStepNames = () => config?.steps.map( ( step ) => step.id ) || [];

  const getNextStepNumber = () => {
    const stepNames = getStepNames();
    const maxNo = Math.max(
      0,
      ...stepNames
        .map( ( name ) => name.match( /.*_([0-9]+)$/ )?.[1] )
        .filter( Boolean )
        .map( Number )
    );
    return maxNo + 1;
  };

  // Event handlers
  const handleVersionChange = ( version: string ) => {
    setVersionNext( parseInt( version ) );

    if ( !configIsDirty ) { // No unsaved changes, navigate immediately
      navigate( `/agents/edit?${ new URLSearchParams( { id: qId, version } ) }` );
    } else { // Has unsaved changes, show confirmation modal
      setShowVersionChangeModal( true );
    }
  };

  const handleVersionChangeConfirm = () => {
    setShowVersionChangeModal( false );
    setVersionNext( undefined );
    setValidationErrors( {} ); // Clear validation errors when switching versions
    navigate( `/agents/edit?${ new URLSearchParams( { id: qId, version: String( versionNext ) } ) }` );
  };

  const handleCreateVersion = () => {
    const nextVersion =
      Math.max( ...( config?.versions || [] ).map( ( el ) => parseInt( el ) ) ) + 1;
    setVersionNext( nextVersion );
    setShowCreateVersionModal( true );
  };

  const handleCreateVersionConfirm = async() => {
    if ( isSaving || !versionNext ) return;

    const name = nameRef.current?.value || config?.name;
    const description = descriptionRef.current?.value || config?.description;

    await handleSave( { name, description }, versionNext );
    setShowCreateVersionModal( false );
    setVersionNext( undefined );
  };

  const handleClickNavAgentDetails = () => {
    const url = `/agents/details?${ new URLSearchParams( { id: qId } ) }`;
    navigate( url );
  };

  const handleSaveInEditor = () => {
    const name = nameRef.current?.value || config?.name;
    const description = descriptionRef.current?.value || config?.description;
    handleSave( { name, description } );
  };

  const handleSave = async( configEdits: Partial<AgentConfig> = {}, version?: number ) => {
    if ( !config || !nodes || isSaving ) return;

    setIsSaving( true );

    const nextConfig = { ...config, ...configEdits };

    // Update positions
    nodes.forEach( ( node, i ) => {
      if ( !node.data.label && nextConfig.steps[i] ) {
        nextConfig.steps[i].position = node.position;
      }
    } );

    const versionAfterSave = version?.toString() || versionCurrent.toString();

    try {
      const args: AgentVersionUpdateArgs = {
        ...nextConfig,
        agentId: qId,
        version: versionAfterSave
      };

      await APIGateway.agents.agentVersionUpdate( args );
      toast.success( 'Changes saved!' );

      // Update configOrig to reflect saved state (clears dirty flag)
      setConfigOrig( JSON.stringify( nextConfig ) );

      // Navigate to the saved version - this will refetch data if version changed
      const route = `/agents/edit?${ new URLSearchParams( { id: qId, version: versionAfterSave } ) }`;
      if ( versionAfterSave !== versionCurrent.toString() ) {
        // Creating new version - navigate to it
        navigate( route );
      } else {
        // Saving current version - replace URL to trigger any hooks but stay on same page
        navigate( route, { replace: true } );
      }
    } catch( e ) {
      toast.error( 'Failed to save changes' );
    } finally {
      setIsSaving( false );
    }
  };

  const handleStepAdd = ( type: AgentConfigStepTypeEnum ) => {
    if ( !config ) return;

    const nextStepNo = getNextStepNumber();
    let newStep = null;
    if ( type == AgentConfigStepTypeEnum.API )
      newStep = createStep[type]( nextStepNo );
    if ( type == AgentConfigStepTypeEnum.LLM )
      newStep = createStep[type]( nextStepNo );
    if ( type == AgentConfigStepTypeEnum.VECTORDB )
      newStep = createStep[type]( nextStepNo );
    if ( type == AgentConfigStepTypeEnum.AGENT )
      newStep = createStep[type]( nextStepNo );
    if ( newStep == null ) throw new Error( 'Invalid Step Type being added' );

    if ( !newStep ) {
      toast.error( 'Unable to add new step. Step type does not exist' );
      return;
    }

    // Find the rightmost node and position new node above and to the right
    if ( nodes.length > 0 ) {
      const rightmostNode = nodes.reduce( ( rightmost, node ) => {
        return node.position.x > rightmost.position.x ? node : rightmost;
      } );

      // Position new node 80px above and 50px to the right of rightmost node
      newStep.position = {
        x: rightmostNode.position.x + 50,
        y: rightmostNode.position.y - 80
      };
    } else {
      // Fallback if no nodes exist (shouldn't happen with START/END nodes)
      newStep.position = DEFAULT_NODE_POSITION;
    }

    dispatch( { type: AgentEditActionType.STEP_ADD, step: newStep } );
  };

  const handleNodeDelete = () => {
    if ( !config || activeStepIndex === undefined ) return;

    const stepId = config.steps[activeStepIndex].id;
    dispatch( { type: AgentEditActionType.STEP_DELETE, stepId } );
    setActiveStepIndex( undefined );
  };

  const handleNodeSelect = (
    _e: React.MouseEvent,
    node: Node<AgentConfigStep>
  ) => {
    const stepIndex = getStepIndex( node.id );
    setActiveStepIndex( stepIndex );
  };

  const handleNodeDragStop = (
    _e: React.MouseEvent,
    node: Node<AgentConfigStep>
  ) => {
    if ( !config ) return;

    const stepIndex = getStepIndex( node.id );
    if ( stepIndex !== undefined ) {
      dispatch( { type: AgentEditActionType.STEP_MOVE, stepId: node.id, position: node.position } );
    }
  };

  const handleClickNavTest = () => {
    const url = `/agents/playground?${ new URLSearchParams( { i: qId, t: AgentTypeEnum.LOW_CODE, v: qVersion } ) }`;
    navigate( url );
  };

  const handleEditorCloseWithoutSave = () => {
    if ( configOrig ) dispatch( { type: AgentEditActionType.ROLLBACK, config: JSON.parse( configOrig ) } );
    setValidationErrors( {} ); // Clear validation errors when rolling back
    exitEditState();
    setShowSaveEditsModal( false );
  };

  const handleEditorCloseWithSave = () => {
    handleSaveInEditor();
    exitEditState();
    setShowSaveEditsModal( false );
  };

  const exitEditState = () => {
    setIsEditable( false );
    setIsFullScreen( false );
    setIsPreview( false );
    setActiveStepIndex( undefined );
    setShowSaveEditsModal( false );
    // fitView will be automatically called by the ResizeObserver when container size changes
  };

  // Render helpers
  const renderModals = useMemo( () => (
    <>
      {showVersionChangeModal && (
        <VersionChangeModal
          onConfirm={ handleVersionChangeConfirm }
          onCancel={ () => {
            setShowVersionChangeModal( false );
            setVersionNext( undefined );
          } }
        />
      )}
      {showCreateVersionModal && (
        <VersionCreateModal
          onConfirm={ handleCreateVersionConfirm }
          onCancel={ () => {
            setShowCreateVersionModal( false );
            setVersionNext( undefined );
          } }
        />
      )}
      {showSaveEditsModal && config && (
        <SaveEditsModal
          agentName={ config.name }
          agentVersion={ versionCurrent }
          hasErrors={ hasErrors }
          onCancel={ () => setShowSaveEditsModal( false ) }
          onClose={ handleEditorCloseWithoutSave }
          onSave={ handleEditorCloseWithSave }
        />
      )}
    </>
  ), [ showVersionChangeModal, showCreateVersionModal, showSaveEditsModal, config, versionCurrent ] );

  const handleEditorCloseButton = () => {
    if( configIsDirty ) {
      setShowSaveEditsModal( true ); // unsaved changes
    } else {
      exitEditState();
    }
  };

  const renderFlow = () => {
    const snapGrid: [number, number] = [ 20, 20 ];
    const defaultViewport = { x: 0, y: 0, zoom: 1.5 };

    return (
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <FlowWrapper containerRef={ containerRef } fitViewRef={ fitViewRef } />
        <div
          ref={ containerRef }
          data-testid={ clsx( {
            normal_screen_editor: !isFullScreen && !isPreview,
            full_screen_editor: isFullScreen,
            preview_editor: isPreview
          } ) }
          className={ clsx( {
            [ styles.normal_screen_editor ]: !isFullScreen && !isPreview,
            [ styles.full_screen_editor ]: isFullScreen,
            [ styles.preview_editor ]: isPreview
          } ) }
        >
          <div
            className={ styles.editor_child }
            style={ { width: isPreview ? 'calc(100% - 400px)' : '100%' } }
          >
            <ReactFlow
              className={ styles.react_flow }
              defaultEdgeOptions={ {
                type: 'smart',
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: 'var(--xy-edge-stroke-default)',
                  orient: '0'
                }
              } }
              edgeTypes={ edgeTypes }
              defaultViewport={ defaultViewport }
              edges={ edges }
              fitView
              nodes={ nodes }
              nodeTypes={ nodeTypes }
              onEdgesChange={ onEdgesChange }
              onNodesChange={ onNodesChange }
              onNodeClick={ handleNodeSelect }
              onNodeDragStop={ handleNodeDragStop }
              snapGrid={ snapGrid }
              snapToGrid={ true }
              nodesDraggable={ isEditable }
            >
              <Background
                gap={ 16 }
                size={ 1 }
                variant={ BackgroundVariant.Dots }
              />
              <Controls
                className={ styles.react_flow_controls }
                data-testid='agent-edit-flow-controls'
                position='bottom-left'
                showFitView={ false }
                showInteractive={ false }
                showZoom={ false }
              >
                <ControlButton
                  aria-label='Zoom in'
                  data-testid='agent-edit-zoom-in-button'
                  onClick={ () => zoomIn() }
                >
                  <AiOutlinePlusCircle size={ 50 } />
                </ControlButton>
                <ControlButton
                  aria-label='Zoom out'
                  data-testid='agent-edit-zoom-out-button'
                  onClick={ () => zoomOut() }
                >
                  <AiOutlineMinusCircle size={ 24 } />
                </ControlButton>
                <ControlButton
                  aria-label='Fit view'
                  data-testid='agent-edit-fit-view-button'
                  onClick={ () => fitView( { padding: 0.2 } ) }
                >
                  <MdOutlineCenterFocusStrong size={ 24 } />
                </ControlButton>
              </Controls>
            </ReactFlow>

            <div className={ styles.toolbar }>
              <div className={ styles.top }>
                <p className={ styles.agent_name } data-testid='agent-edit-agent-name'>
                  { config?.name }
                </p>
              </div>

              <div className={ styles.bottom }>
                {
                  !isEditable && configIsEditable && (
                    <>
                      <button
                        onClick={ () => {
                          setIsEditable( true );
                          setIsFullScreen( true );
                        } }
                      >
                        <FaRegEdit /> Edit Agent Steps
                      </button>
                    </>
                  )
                }

                {
                  isEditable && (
                    <>
                      <Menu
                        data-testid='add-node-button'
                        variant='menu'
                        icon={ (
                          <>
                            <FiPlusCircle />
                            <p>Add a Node</p>
                          </>
                        ) }
                      >
                        {
                          addableNodeTypes.map(
                            nodeType => {
                              return (
                                <MenuItem
                                  data-testid={ `add-node-${ nodeType }` }
                                  leadingIcon={ getNodeTypeIcon( nodeType ) }
                                  onClick={ () => handleStepAdd( nodeType ) }
                                  value={ getNodeTypeLabel( nodeType ) }
                                  classname={ styles.menuitem_button }
                                  description={ getNodeTypeAddDescription( nodeType as ADDABLE_NODE_TYPES ) }
                                />
                              );
                            }
                          )
                        }
                      </Menu>
                    </>
                  )
                }
              </div>
            </div>

            {
              isEditable && (
                <div className={ styles.right_menu }>
                  <Button
                    data-disabled-message={ hasErrors ? 'Cannot save. Fix validation errors first' : undefined }
                    data-testid='agent-edit-save-button'
                    disabled={ hasErrors }
                    onClick={ () => handleSaveInEditor() }
                    variant='primary'
                  >
                    Save
                  </Button>
                  {
                    isPreview
                      ? (
                          <Button
                            data-testid='agent-edit-try-agent-button'
                            onClick={ () => setIsPreview( false ) }
                            variant='secondary'
                          >
                            Close Try Agent
                          </Button>
                        )
                      : (
                          <Button
                            data-disabled-message={ hasErrors ? 'Cannot preview. Fix validation errors first' : undefined }
                            data-testid='agent-edit-try-agent-button'
                            disabled={ hasErrors }
                            onClick={ () => setIsPreview( true ) }
                            variant='secondary'
                          >
                            Try Agent
                          </Button>
                        )
                  }
                  <Button
                    data-testid='agent-edit-close-button'
                    onClick={ handleEditorCloseButton }
                    variant='secondary'
                  >
                    Close
                  </Button>
                </div>
              )
            }

            {
              config
                && activeStepIndex !== undefined
                && config.steps[activeStepIndex]
                && (
                  <ConfigEditPanel
                    key={ activeStepIndex }
                    context={ config.context }
                    isFullScreen={ isFullScreen }
                    editable={ isEditable }
                    modelNames={ models }
                    onClose={ () => {
                      setActiveStepIndex( undefined );
                    } }
                    onDelete={ handleNodeDelete }
                    step={ config.steps[activeStepIndex] }
                    stepNames={ getStepNames() }
                  />
                )
            }
          </div>
          {
            isPreview && (
              <div className={ styles.previewPane }>
                <LowCodeChat config={ config! }></LowCodeChat>
              </div>
            )
          }
        </div>
      </div>
    );
  };

  const renderTabs = useMemo( () => {
    if ( !config ) return null;

    return (
      <div className={ clsx( 'layout__row', 'pad' ) }>
        <Tabs className='tabs'>
          <TabList>
            <Tab>Recent Traces</Tab>
            <Tab>Performance</Tab>
          </TabList>
          <TabPanel className='tabs tab_panel'>
            <TraceView agentId={ qId } editable={ isEditable } version={ qVersion } />
          </TabPanel>
          <TabPanel className='tabs tab_panel'>
            <PerformanceView />
          </TabPanel>
        </Tabs>
      </div>
    );
  }, [ config, qId, isEditable, qVersion ] );

  if ( isLoading ) {
    return (
      <div className={ `page ${ isFullScreen ? styles.full_screen : '' }` }>
        <ToastContainer />
        <Loader />
      </div>
    );
  }

  if ( !config ) return null;

  return (
    <div className={ `page ${ isFullScreen ? styles.full_screen : '' }` }>
      <ToastContainer />
      { renderModals }

      <InfoView
        config={ config }
        versionCurrent={ versionCurrent }
        versionNext={ versionNext }
        configIsEditable={ configIsEditable }
        onDetails={ handleClickNavAgentDetails }
        onVersionChange={ handleVersionChange }
        onEdit={ () => setIsEditable( true ) }
        onTest={ handleClickNavTest }
        onCreateVersion={ handleCreateVersion }
        validationErrors={ validationErrors }
      />

      { renderFlow() }
      { renderTabs }
    </div>
  );
}

// Main component that provides ReactFlow and AgentEdit context
export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <ReactFlowProvider>
        <AgentEditProvider>
          <AgentEditPage />
        </AgentEditProvider>
      </ReactFlowProvider>
    </Suspense>
  );
}
